import clsx from 'clsx';
import { Image, Video, Link } from '~/shared/components';
import styles from './HeroImage.module.scss';
import { VideoMedia, VideoSchema } from '~/lib';
import { ReactNode } from 'react';
import { CustomCSSProperties } from '~/templates/blocks/components/M11Hero/M11Hero';
import { extractVideoMetadataFromModule } from '../Video/get-video-meta-data';

const HERO_SIZES = '(min-width: 1024px) 400px, (min-width: 520px) 500px, 100vw';

type HeroImageProps = VideoSchema & {
    image: {
        id?: number;
        name?: string;
        crops?: any[];
        src: string;
        extension?: string;
        height?: number;
        width?: number;
        ratio?: number;
        type?: string;
    };
    cta?: {
        text?: string;
        title?: string;
        url?: string;
        id?: string;
        target?: string;
    };
    imageAltText?: string;
    watermark: string;
    imageOnBackground: boolean;
    video?: VideoMedia;
    title?: string;
    watermarkElement?: ReactNode;
    customProperties?: CustomCSSProperties;
    gradientHighlight: boolean;
    priority?: boolean;
};

export const HeroImage = ({
    image,
    imageAltText,
    watermark,
    imageOnBackground,
    video,
    title,
    customProperties,
    watermarkElement,
    gradientHighlight,
    priority,
    cta,
    ...moduleProps
}: HeroImageProps) => {
    const isGif =
        image?.extension?.toLowerCase() === 'gif' || (image?.src && /\.gif/i.test(image.src));

    return (
        <div className={styles.media} style={customProperties}>
            <div
                className={clsx(styles.mediaWrapper, {
                    [styles.isVerticalOnMobile]: !!video?.src || isGif,
                })}
            >
                {imageOnBackground && (
                    <div
                        className={clsx(styles.watermark, {
                            [styles.watermarkPercentMobile]:
                                imageOnBackground && watermark === 'Percent',
                            [styles.watermarkLogoImage]: imageOnBackground && watermark === 'Logo',
                        })}
                    >
                        {watermarkElement}
                    </div>
                )}

                <div className={clsx(gradientHighlight ? styles.shadowGradient : styles.shadow)} />

                {video ? (
                    <div
                        className={clsx(styles.mediaElement, {
                            [styles.imageOnBackground]: imageOnBackground,
                        })}
                    >
                        <Video
                            name={title}
                            muted={video.mute}
                            controls={false}
                            src={video.src}
                            loop={video.loop}
                            playing={video.autoPlay}
                            posterSrc={image?.src}
                            cover={true}
                            theaterMode={video.theaterMode}
                            metadata={extractVideoMetadataFromModule(moduleProps)}
                        />
                    </div>
                ) : cta?.url ? (
                    <Link
                        title={cta.title}
                        noHover
                        href={cta.url}
                        target={cta.target}
                        css={{
                            display: 'flex',
                            height: '100%',
                            width: '100%',
                            position: 'relative',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <Image
                            src={image.src}
                            alt={imageAltText}
                            className={clsx(styles.mediaElement, {
                                [styles.imageOnBackground]: imageOnBackground,
                            })}
                            layout="fill"
                            sizes={HERO_SIZES}
                            objectFit={imageOnBackground ? 'contain' : 'cover'}
                            objectPosition="center"
                            priority={priority}
                        />
                    </Link>
                ) : (
                    <Image
                        src={image.src}
                        alt={imageAltText}
                        className={clsx(styles.mediaElement, {
                            [styles.imageOnBackground]: imageOnBackground,
                        })}
                        layout="fill"
                        sizes={HERO_SIZES}
                        objectFit={imageOnBackground ? 'contain' : 'cover'}
                        objectPosition="center"
                        priority={priority}
                    />
                )}
            </div>
        </div>
    );
};
